import type { ChangeEvent } from 'react';
import { useDebouncedValue } from '@/hooks/useDebounceValue';
import { useEvent } from '@/hooks/useEvent';
import { useSafeState } from '@/hooks/useSafeState/useSafeState';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import { SEARCH_QUERY_KEY } from '../../consts/search-const';

interface UseGlobalSearchInputOptions {
  isSyncEnabled?: boolean;
}

export const useGlobalSearchInput = ({ isSyncEnabled = true }: UseGlobalSearchInputOptions = {}) => {
  const [params, updateSearchParams] = useSearchQuery();
  const urlValue = params.get(SEARCH_QUERY_KEY) ?? '';
  const [value, setValue] = useSafeState(urlValue);
  const [previousUrlValue, setPreviousUrlValue] = useSafeState(urlValue);

  const syncValueToUrl = useEvent((next: string) => {
    if (!isSyncEnabled) return;
    updateSearchParams({ [SEARCH_QUERY_KEY]: next || null });
  });

  useDebouncedValue(value, 500, syncValueToUrl);

  if (urlValue !== previousUrlValue) {
    setPreviousUrlValue(urlValue);
    setValue(urlValue);
  }

  const onChange = useEvent((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  });

  const reset = useEvent(() => {
    setValue('');
  });

  return { value, onChange, reset };
};
